import { useEffect, useRef, useState } from 'react'
import { Modal, Page, FooterOrderResume } from 'components'
import { useSelector, useDispatch } from 'store/store'
import SimulateOrder from './components/SimulateOrder'
import {
  clearSimulationAlteredMaterials,
  setPersistFormData,
  setSimulateOrder,
  setRequestSimulate
} from 'store/SalesOrderContext/salesOrderSlice'
import { useNavigate, useParams } from 'react-router-dom'
import { SimulateDraft } from 'container/SalesOrder/interfaces'
import { getUser } from 'commons/utils/user'
import * as DraftOV from 'services/draft'
import {
  RequestBodyConfirmDraft,
  RequestBodySimulateDraft
} from 'services/draft/interfaces'
import { getOrderReport } from 'services/manage_ov'

import { formatServerDate } from 'commons/utils/date'
import { toast } from 'react-toastify'
import { FormProvider, useForm } from 'react-hook-form'
import { URL_CONST } from 'constants/urls'
import ConfigOrder from 'container/SalesOrder/components/ConfigOrder'

import {
  setSalesOrderInitialTab,
  updateOrderStep
} from 'store/StructuralContext/structuralSlice'
import { TypesValidations } from 'constants/enums'
import OrderOverviewModal from 'container/SalesOrder/components/OrderOverviewModal'
import statusRascunho from 'constants/statusRascunho'
import { ModalExtraMaterials } from 'container/SalesOrder/components/ModalExtraMaterials'

const SalesOrderSimulate = () => {
  const { orderNumber } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { simulateOrder, draftBody, persistFormData } = useSelector(
    (store) => store.salesOrder
  )
  const isMounted = useRef(false)

  const [modalEditConfirm, setModalEditConfirm] = useState(false)
  const [openOrderOverview, setOpenOrderOverview] = useState<boolean>(false)
  const [openExtraMaterials, setOpenExtraMaterials] = useState<boolean>(false)
  const [isLoadingGenerateOV, setIsLoadingGenerateOV] = useState(false)
  const [loadingProceed, setIsLoadingProceed] = useState(false)
  const [loadingReport, setLoadingReport] = useState(false)

  const { orderStep } = useSelector((store) => store.structure)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (orderStep === 0) {
      navigate(`${URL_CONST.ORDERS}/${orderNumber}`)
    }
  }, [orderStep])

  const methods = useForm({
    defaultValues: persistFormData
  })
  const { getValues } = methods

  const handleEditConfirm = () => {
    dispatch(setPersistFormData({ ...getValues() }))
    dispatch(updateOrderStep(0))
    navigate(`${URL_CONST.ORDERS}/${orderNumber}`)
  }

  const handleReturnToEdit = () => {
    dispatch(updateOrderStep(0))
    navigate(`${URL_CONST.ORDERS}/${orderNumber}`)
    setOpenExtraMaterials(false)
  }

  const clearAlteredStatus = () => {
    dispatch(clearSimulationAlteredMaterials())
  }

  const handleEditAction = () => {
    clearAlteredStatus()
    setModalEditConfirm(true)
  }

  const continueExtraMaterials = () => {
    setOpenExtraMaterials(false)
    setOpenOrderOverview(true)
  }

  const returnOrderOverview = () => {
    setOpenOrderOverview(false)
  }

  const hasOV = () => !!draftBody?.totalizador?.qt_tota_item_soli

  const handleSimulateProceed = async () => {
    setIsLoadingProceed(true)
    const params: RequestBodySimulateDraft = {
      nr_simu: simulateOrder?.nr_simu || '',
      nr_pedi_rasc: orderNumber ?? '',
      dt_alte_antr: formatServerDate(),
      cd_usua_alte: getUser(),
      ds_item_pedi_rasc: [],
      cd_matr_anls_pedi_rasc: []
    }

    const data: SimulateDraft = await DraftOV.postSimulateDraft(params)

    if (!data) {
      setIsLoadingProceed(false)
      return
    }
    dispatch(setPersistFormData({ ...getValues() }))
    dispatch(setSimulateOrder(data))
    setIsLoadingProceed(false)

    if (data?.materiais?.every((material) => material.stat === statusRascunho.ANSWERED)) {
      setOpenOrderOverview(true)
    } else {
      setOpenExtraMaterials(true)
    }
  }

  const handleGenerateOVs = async () => {
    if (!simulateOrder) return
    setIsLoadingGenerateOV(true)

    if (draftBody.fl_pedi_antp === TypesValidations.YES) {
      clearAlteredStatus()
      setOpenOrderOverview(false)
      setIsLoadingGenerateOV(false)
      navigate(`${URL_CONST.MANAGE_ORDER}/${orderNumber}`)
      toast.success('Pedido de Pré-venda solicitado com sucesso')
    } else {
      const params: RequestBodyConfirmDraft = {
        dt_alte_antr: formatServerDate(),
        nr_simu: simulateOrder.nr_simu,
        cd_usua_alte: getUser()
      }

      try {
        await DraftOV.postConfirmDraft(params)

        if (simulateOrder.resumo.vl_tota_orde_gera === 0) {
          toast.info('Nenhum material foi solicitado')
          dispatch(setSalesOrderInitialTab(1))
        } else {
          toast.success('Materiais solicitados com sucesso')
          dispatch(setSalesOrderInitialTab(0))
        }

        dispatch(setRequestSimulate({} as RequestBodySimulateDraft))
        navigate(`${URL_CONST.MANAGE_ORDER}/${orderNumber}`)
      } catch {
        toast.error('Erro ao solicitar materiais')
      } finally {
        clearAlteredStatus()

        if (isMounted.current) {
          setOpenOrderOverview(false)
        }

        setIsLoadingGenerateOV(false)
      }
    }
  }

  const downloadReport = async (orderID: string) => {
    try {
      setLoadingReport(true)
      const report = await getOrderReport(orderID)
      // TODO: Replace with utils/downlodFile({file, fileName, fileType})
      const url = window.URL.createObjectURL(new Blob([report]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `relatorio_pedido_${orderID}.xlsx`)
      link.click()
      toast.success('Relatório baixado com sucesso!')
    } catch {
      toast.error('Não foi possível baixar o relatório. Tente novamente mais tarde.')
    } finally {
      setLoadingReport(false)
    }
  }

  return (
    <>
      {orderStep === 1 && (
        <FormProvider {...methods}>
          <Page
            fullwidth
            renderHeaderComponent={
              draftBody?.nr_pedi_rasc && <ConfigOrder hasOVs={hasOV()} step={1} />
            }
          >
            {simulateOrder && (
              <>
                <SimulateOrder data={simulateOrder} />
                <FooterOrderResume
                  type="proceed"
                  totalOfPairs={simulateOrder.qt_tota_atnd_conv}
                  totalOfOrder={simulateOrder.vl_tota_atnd}
                  onProceed={handleSimulateProceed}
                  isProceedLoading={loadingProceed}
                  onEdit={handleEditAction}
                  isLoadingReport={loadingReport}
                  onDownloadReport={() => downloadReport(simulateOrder.nr_pedi_rasc)}
                />
              </>
            )}
            <Modal
              open={modalEditConfirm}
              setOpen={setModalEditConfirm}
              title="Editar materiais"
              description="Ao voltar para a configuração do pedido, as possíveis alterações de volume realizadas não serão salvas. Deseja prosseguir mesmo assim?"
              showReturn
              returnText="Não"
              buttonText="Editar materiais"
              buttonAction={() => {
                setModalEditConfirm(false)
                handleEditConfirm()
              }}
              returnAction={() => setModalEditConfirm(false)}
              closeAction={() => setModalEditConfirm(false)}
            />
            <OrderOverviewModal
              open={openOrderOverview}
              setOpen={setOpenOrderOverview}
              onConfirm={handleGenerateOVs}
              loadingConfirm={isLoadingGenerateOV}
              onReturn={returnOrderOverview}
            />
            {openExtraMaterials && (
              <ModalExtraMaterials
                open={openExtraMaterials}
                setOpen={setOpenExtraMaterials}
                onConfirm={continueExtraMaterials}
                onReturn={handleReturnToEdit}
              />
            )}
          </Page>
        </FormProvider>
      )}
    </>
  )
}

export default SalesOrderSimulate
